import { useRef, useState, useCallback, useEffect } from "react";
import { Flex, Search, Text, Button, isNullish } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { FieldMultiSelection } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/MappingConfiguration/FieldMultiSelection";
import { appstrp } from "@coreapp/utils/appstrp";
import {
  kReservedFields,
  restrictedInternalFields,
  kPositionCountFields,
  kEmployeeCountFields
} from "@coreapp/constants";
import { MAPPING_TEST_PREFIX } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/MappingConfiguration/Utils";

export const PositionHolderFields = props => {
  const [queryString, setQueryString] = useState("");
  const [fieldCategories, setFieldCategories] = useState({
    positionFields: [],
    holderFields: []
  });

  // Create a state to track selection changes
  const [selectedFields, setSelectedFields] = useState([]);
  const multiSortRef = useRef(null);

  const syncFieldCategories = () => {
    let positionFields = props.chartConfig?.positionFields || [];
    let holderFields = props.chartConfig?.holderFields || [];

    // Explicitly filter out employee count fields from position fields
    positionFields = positionFields.filter(field => !kEmployeeCountFields.includes(field));

    // Explicitly filter out position count fields from holder fields
    const positionCountWithHolder = [...kPositionCountFields, "Holder Count"];
    holderFields = holderFields.filter(field => !positionCountWithHolder.includes(field));

    setFieldCategories({
      positionFields: [...new Set(positionFields.filter(field => !isNullish(field)))],
      holderFields: [...new Set(holderFields.filter(field => !isNullish(field)))]
    });
  };

  useEffect(() => {
    syncFieldCategories();
  }, [props.chartConfig]);

  const handleFieldsChange = dispatch => {
    const nextState = typeof dispatch === "function" ? dispatch(fieldCategories) : dispatch;

    props.onUpdate(
      {
        positionFields: nextState.positionFields,
        holderFields: nextState.holderFields
      },
      ["positionFields", "holderFields"]
    );

    setFieldCategories(nextState);
  };

  // Update our local selectedFields state whenever the selection changes in the child
  const handleSelectionChange = useCallback(newSelectedIds => {
    setSelectedFields(newSelectedIds);
  }, []);

  // Initialize selectedFields from the ref if available
  useEffect(() => {
    if (multiSortRef.current?.selectedIds) {
      setSelectedFields(multiSortRef.current.selectedIds);
    }
  }, []);

  const handleAddToHolderFields = () => {
    if (selectedFields.length === 0) return;

    const nextState = {
      ...fieldCategories,
      holderFields: [
        ...fieldCategories.holderFields,
        ...selectedFields.filter(field => !fieldCategories.holderFields.includes(field))
      ],
      positionFields: fieldCategories.positionFields.filter(
        field => !selectedFields.includes(field)
      )
    };

    handleFieldsChange(nextState);
  };

  const handleAddToPositionFields = () => {
    if (selectedFields.length === 0) return;

    const nextState = {
      ...fieldCategories,
      positionFields: [
        ...fieldCategories.positionFields,
        ...selectedFields.filter(field => !fieldCategories.positionFields.includes(field))
      ],
      holderFields: fieldCategories.holderFields.filter(field => !selectedFields.includes(field))
    };

    handleFieldsChange(nextState);
  };

  const handleAddToBothFields = () => {
    if (selectedFields.length === 0) return;

    const nextState = {
      ...fieldCategories,
      positionFields: [
        ...fieldCategories.positionFields,
        ...selectedFields.filter(field => !fieldCategories.positionFields.includes(field))
      ],
      holderFields: [
        ...fieldCategories.holderFields,
        ...selectedFields.filter(field => !fieldCategories.holderFields.includes(field))
      ]
    };

    handleFieldsChange(nextState);
  };

  const handleToggleAll = target => () => {
    const allFields = getAllFieldNames(props);

    const nextState = {
      positionFields: [],
      holderFields: []
    };

    nextState[target] = allFields;

    handleFieldsChange(nextState);
  };

  const handleInput = query => {
    setQueryString(query);
  };

  const getFilteredFieldCategories = () => {
    if (!queryString) {
      return fieldCategories;
    }

    const lowerCaseQuery = queryString.toLowerCase();
    const filterFields = fieldNames => {
      return fieldNames.filter(fieldName => fieldName.toLowerCase().includes(lowerCaseQuery));
    };

    return {
      positionFields: filterFields(fieldCategories.positionFields),
      holderFields: filterFields(fieldCategories.holderFields)
    };
  };

  const getRestrictedFieldIds = () => {
    return [...restrictedInternalFields, ...kReservedFields];
  };

  const getButtonStates = () => {
    if (selectedFields.length === 0) {
      return {
        position: false,
        employee: false,
        both: false
      };
    }

    const mappedFieldNames = props.chartConfig?.fields
      ? Object.values(props.chartConfig.fields)
      : [];

    const hasAnyMapped = selectedFields.some(field => mappedFieldNames.includes(field));

    const hasAnyDualMapped = selectedFields.some(
      field =>
        fieldCategories.positionFields.includes(field) &&
        fieldCategories.holderFields.includes(field)
    );

    if (hasAnyDualMapped) {
      return {
        position: true,
        employee: true,
        both: true
      };
    }

    return {
      position: !hasAnyMapped,
      employee: !hasAnyMapped,
      both: true
    };
  };

  const buttonStates = getButtonStates();

  return (
    <Flex height={"100%"} width={"100%"} direction="column" gap="8px">
      <Flex padding={"6px"}>
        <Text>
          {appstr(
            "Select fields and assign them as either holder fields, position fields, or both"
          )}
        </Text>
      </Flex>
      <Flex justifyContent={"space-between"}>
        <Flex gap={"12px"} alignItems={"center"}>
          <Text size={"small"} modifier={"bold"}>
            {appstrp("Assign as", ":")}
          </Text>
          <Flex gap="12px" justifyContent="flex-start" padding="8px">
            <Button
              disabled={!buttonStates.position}
              onClick={handleAddToPositionFields}
              data-testid="add-to-position-fields-btn"
              modifier={"secondary"}
            >
              {appstr("Position")}
            </Button>
            <Button
              disabled={!buttonStates.employee}
              onClick={handleAddToHolderFields}
              data-testid="add-to-holder-fields-btn"
              modifier={"secondary"}
            >
              {appstr("Employee")}
            </Button>
            <Button
              disabled={!buttonStates.both}
              onClick={handleAddToBothFields}
              data-testid="add-to-both-fields-btn"
              modifier={"secondary"}
            >
              {appstr("Both")}
            </Button>
          </Flex>
        </Flex>
        <Flex width={"400px"}>
          <Search
            dataTestId={`${MAPPING_TEST_PREFIX}-holder-search`}
            size="medium"
            placeholder={appstr("findfield")}
            onInput={handleInput}
          />
        </Flex>
      </Flex>
      <Flex flex={1} width="100%" overflow="hidden">
        <FieldMultiSelection
          ref={multiSortRef}
          dataTestIdPrefix="field-binning"
          hasFilter={queryString.length > 0}
          onToggleAll={handleToggleAll}
          onChange={handleFieldsChange}
          filteredItems={getFilteredFieldCategories()}
          items={fieldCategories}
          disabledIds={getRestrictedFieldIds()}
          onSelectionChange={handleSelectionChange}
          headerMap={{
            positionFields: appstr("Position fields"),
            holderFields: appstr("Employee fields")
          }}
        />
      </Flex>
    </Flex>
  );
};

export const SystemFields = [
  ...kEmployeeCountFields,
  "SubChart Level",
  "Level",
  ...kPositionCountFields
];

const getAllFieldNames = props => {
  return (props.chartConfig?.headers || []).filter(header => !SystemFields.includes(header));
};
